import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
} from 'react';
import {
  Flex,
  css,
  genesisStyled,
  Box,
  SpacePropsType,
  BodyOne,
  VisuallyHidden,
} from '@leagueplatform/genesis-commons';
import { useIntl } from '@leagueplatform/locales';
import {
  useSwipeGestures,
  SWIPE_ORIENTATION,
  SWIPE_DIRECTION,
} from '@leagueplatform/web-common';
import { tween, easing } from 'popmotion';
import {
  GDSStyleObject,
  StackLayout,
  useTextDirectionality,
} from '@leagueplatform/genesis-core';
import { LinkCTAProps } from '#types/carousel-common.types.ts';
import { CTALink } from './carousel-cta-link.component';
import { ProgressDots } from './carousel-progress-dots.component';
import {
  BackNavButton,
  ForwardNavButton,
} from './carousel-nav-button.component';

type DefaultCarouselProps = {
  name: string;
  children: React.ReactNode;
  spaceBetweenCards?: SpacePropsType;
  heading?: React.ReactNode;
  description?: React.ReactNode;
  sideArrows?: boolean;
  showProgressDots?: boolean;
  BackNavButtonAnalyticsFn?: () => void;
  ForwardNavButtonAnalyticsFn?: () => void;
  /**
   * @deprecated  use `BackNavButtonAnalyticsFn` instead
   */
  LeftNavButtonAnalyticsFn?: () => void;
  /**
   * @deprecated  use `ForwardNavButtonAnalyticsFn` instead
   */
  RightNavButtonAnalyticsFn?: () => void;
  onIndexChange?: (index: number) => void;
  titleMarginBottom?: SpacePropsType;
  listStyles?: GDSStyleObject;
  className?: string;
};

type CarouselProps = DefaultCarouselProps & LinkCTAProps;

interface CarouselRefType extends HTMLUListElement {
  // offsetWidth extended from HTMLUListElement
  children: HTMLCollectionOf<HTMLLIElement>;
}

enum NAV_BUTTON {
  forward = 'FORWARD',
  back = 'BACK',
}

export const shouldShowNavigation = ({
  clientWidth,
  scrollWidth,
}: CarouselRefType) => scrollWidth > clientWidth;

const CarouselInnerWrapper = genesisStyled(Flex)(
  ({ spaceBetweenCards, listStyles }) =>
    css({
      justifyContent: 'space-between',
      '& > *': {
        marginInlineEnd: spaceBetweenCards,
        '&:last-child': {
          marginInlineEnd: 0,
        },
      },
      // Adding list styles to add styles to the list items in case of customizations
      '& > li': {
        ...listStyles,
      },
    }),
);

export const Carousel = forwardRef<HTMLElement, CarouselProps>(
  (
    {
      name,
      children,
      spaceBetweenCards = 'one',
      heading,
      description,
      linkOnClick,
      linkAriaLabel,
      linkCta,
      linkUrl,
      BackNavButtonAnalyticsFn: originalBackNavButtonAnalyticsFn,
      ForwardNavButtonAnalyticsFn: originalForwardNavButtonAnalytics,
      LeftNavButtonAnalyticsFn,
      RightNavButtonAnalyticsFn,
      sideArrows = false,
      showProgressDots = false,
      onIndexChange,
      titleMarginBottom = 'oneAndHalf',
      listStyles = {},
      className,
    }: CarouselProps,
    ref,
  ) => {
    const BackNavButtonAnalyticsFn =
      originalBackNavButtonAnalyticsFn ||
      LeftNavButtonAnalyticsFn ||
      (() => null);
    const ForwardNavButtonAnalyticsFn =
      originalForwardNavButtonAnalytics ||
      RightNavButtonAnalyticsFn ||
      (() => null);
    const dir = useTextDirectionality();
    const carouselRef = useRef<CarouselRefType>(null);
    const childrenRef = useRef<HTMLLIElement[]>([]);

    // Navigation Buttons
    const [forwardArrowDisabled, setForwardArrowDisabled] = useState(true);
    const [backArrowDisabled, setBackArrowDisabled] = useState(true);
    const [showNavButtons, setShowNavButtons] = useState(true);
    const swipeDirection = useSwipeGestures(
      carouselRef,
      SWIPE_ORIENTATION.horizontal,
    );

    // Progress Indicator Dots
    const [currentProgressIndex, setCurrentProgressIndex] = useState(0);

    // Carousel Children
    const childrenLength = React.Children.toArray(children).length;
    const [childrenInView, setChildrenInView] = useState<number[]>([]);

    // Screen reader announcement
    const { formatMessage } = useIntl();
    const [slideAnnouncement, setSlideAnnouncement] = useState('');

    const currentCarouselProperties = () => {
      const { current: currentCarouselRef } = carouselRef;

      if (currentCarouselRef) {
        const { scrollLeft, offsetWidth, scrollWidth } = currentCarouselRef;
        /**
         * Scroll positions might be reported with sub-pixel accuracy (e.g., 298.90 or 299.14 instead of 299).
         * Since we only need pixel-level precision, we round up to the nearest whole number.
         */
        const scrollAmt = Math.round(Math.abs(scrollLeft));
        const firstChild = currentCarouselRef.children[0];

        const childrenWidth =
          firstChild.offsetWidth +
          parseInt(getComputedStyle(firstChild).marginInlineEnd, 10);

        // Current index is determined by how far it is scrolled, divided by the width of each card.
        const currentIndex = parseInt(
          (scrollAmt / childrenWidth).toString(),
          10,
        );

        // Scroll card amount is determined by the scroll container divided by the card width.
        const amountOfChildrenInView = Math.round(offsetWidth / childrenWidth);

        const userScroll = offsetWidth + scrollAmt;

        return {
          currentCarouselRef,
          scrollAmt,
          offsetWidth,
          scrollWidth,
          userScroll,
          firstChild,
          childrenWidth,
          currentIndex,
          amountOfChildrenInView,
        };
      }

      return null;
    };

    // Determines which children indexes are currently in view and updates state.
    const updateChildrenInView = useCallback(() => {
      const carouselProperties = currentCarouselProperties();

      if (carouselProperties) {
        const {
          amountOfChildrenInView,
          currentIndex,
          userScroll,
          scrollWidth,
        } = carouselProperties;

        // Used to make sure the indexes returned are accurate when the last of the carousel children is reached.
        const lastChildOffset =
          childrenLength > 1 && userScroll > scrollWidth ? 1 : 0;
        const currentChildIndex = currentIndex + lastChildOffset;

        if (amountOfChildrenInView === 1)
          return setChildrenInView([currentChildIndex]);

        const inView: number[] = [];
        for (let x = 0; x < amountOfChildrenInView; x += 1) {
          inView.push(currentChildIndex + x);
        }

        setChildrenInView(inView);
      }

      return null;
    }, [childrenLength]);

    // Updates the tabindex attribute for carousel items based on if the item is currently in view or not (overflow).
    const updateChildrenTabIndex = useCallback(() => {
      childrenRef.current.forEach((elementRef, index) => {
        const elementInView = childrenInView.find((el) => el === index);

        if (elementInView !== undefined) {
          elementRef
            .querySelector('*[tabindex="-1"]')
            ?.setAttribute('tabindex', '0');
        } else {
          elementRef
            .querySelector('*[tabindex="0"]')
            ?.setAttribute('tabindex', '-1');
        }
      });
    }, [childrenInView, childrenRef]);

    // Disable or enable the Navigation buttons when user scrolls.
    const scrollingEvent = useCallback(() => {
      const carouselProperties = currentCarouselProperties();

      if (carouselProperties) {
        const { scrollAmt, scrollWidth, userScroll } = carouselProperties;

        setBackArrowDisabled(scrollAmt === 0);
        setForwardArrowDisabled(userScroll >= scrollWidth);
      }
    }, []);

    useEffect(() => {
      const carouselProperties = currentCarouselProperties();

      if (carouselProperties) {
        const { scrollWidth, offsetWidth, currentCarouselRef } =
          carouselProperties;

        setForwardArrowDisabled(scrollWidth <= offsetWidth);
        currentCarouselRef.addEventListener('scroll', scrollingEvent);

        // Show navigation buttons only if width of slides > width of container.
        setShowNavButtons(shouldShowNavigation(currentCarouselRef));

        // Store references to carousel children in an array.
        childrenRef.current = childrenRef.current.slice(0, childrenLength);
        updateChildrenInView();

        return () => {
          currentCarouselRef.removeEventListener('scroll', scrollingEvent);
        };
      }

      return () => null;
    }, [children, childrenLength, scrollingEvent, updateChildrenInView]);

    useEffect(() => {
      updateChildrenTabIndex();
    }, [childrenInView, updateChildrenTabIndex]);

    const onResizeCarousel = useCallback(() => {
      const { current: currentCarouselRef } = carouselRef;

      if (currentCarouselRef) {
        const showNavigation = shouldShowNavigation(currentCarouselRef);

        if (showNavigation !== showNavButtons) {
          setShowNavButtons(showNavigation);

          // Trigger the 'scroll' event that the carousel is listening for and reset back to ensure  the carousel is starting on the first slide.
          currentCarouselRef.dispatchEvent(
            new CustomEvent('scroll', { detail: { scrollLeft: 0 } }),
          );
        }
      }
    }, [showNavButtons, carouselRef]);

    useEffect(() => {
      const { current: currentCarouselRef } = carouselRef;

      if (currentCarouselRef) {
        // Listen for resize event(s) only on the carousel instead of the entire window.
        const carouselResize = new ResizeObserver(onResizeCarousel);
        carouselResize.observe(currentCarouselRef);

        return () => {
          carouselResize.disconnect();
        };
      }

      return () => null;
    }, [carouselRef, onResizeCarousel]);

    // return current index when updated
    useEffect(() => {
      if (onIndexChange) {
        if (currentProgressIndex < 0 || currentProgressIndex >= childrenLength)
          return;
        onIndexChange(currentProgressIndex);
      }
    }, [childrenLength, currentProgressIndex, onIndexChange]);

    const scrollTo = (amt = 0, duration = 0) => {
      const { current: currentCarouselRef } = carouselRef;

      const to = dir === 'ltr' ? amt : -amt;

      if (currentCarouselRef) {
        const from = currentCarouselRef.scrollLeft;
        const scroll = tween({
          from,
          to,
          duration,
          ease: easing.easeOut,
        });
        scroll.start({
          update: (left: number) => {
            currentCarouselRef.scrollLeft = left;
          },
          complete: () => {
            updateChildrenInView();
          },
        });
      }
    };

    const handleNavButtonEvent = (type: NAV_BUTTON) => {
      const carouselProperties = currentCarouselProperties();

      if (carouselProperties) {
        const {
          currentIndex,
          amountOfChildrenInView,
          childrenWidth,
          scrollAmt,
          scrollWidth,
          userScroll,
        } = carouselProperties;

        if (type === NAV_BUTTON.forward) {
          // Guard: ignore if already at the end (swipe gestures can still fire)
          if (userScroll >= scrollWidth) return;

          // Determine how far the view needs to scroll to.
          scrollTo(
            (currentIndex + amountOfChildrenInView) * childrenWidth,
            200,
          );
          setCurrentProgressIndex((prev) => {
            const next = prev + 1;
            // Announce the card that just appeared on the trailing edge,
            // clamped to [1, childrenLength] to avoid out-of-bounds values
            const announcedSlide = Math.min(
              next + amountOfChildrenInView,
              childrenLength,
            );
            setSlideAnnouncement(
              formatMessage(
                { id: 'SLIDE_OF_TOTAL' },
                {
                  key: announcedSlide,
                  total: childrenLength,
                },
              ),
            );
            return next;
          });
        } else {
          // Guard: ignore if already at the start (swipe gestures can still fire)
          if (scrollAmt === 0) return;

          // The user is scrolling back. Scroll to the current index's position.
          // On the chance that the user is on the current index's position and wants to scroll back, the carousel would have to scroll to the previous index's position.
          scrollTo(
            scrollAmt % childrenWidth === 0
              ? (currentIndex - amountOfChildrenInView) * childrenWidth
              : currentIndex * childrenWidth,
            200,
          );
          setCurrentProgressIndex((prev) => {
            const next = prev - 1;
            // Announce the card that just appeared on the leading edge,
            // clamped to [1, childrenLength] to avoid out-of-bounds values
            const announcedSlide = Math.max(next + 1, 1);
            setSlideAnnouncement(
              formatMessage(
                { id: 'SLIDE_OF_TOTAL' },
                { key: announcedSlide, total: childrenLength },
              ),
            );
            return next;
          });
        }
      }
    };

    const BackNavButtonEvent = () => {
      handleNavButtonEvent(NAV_BUTTON.back);
      BackNavButtonAnalyticsFn();
    };

    const ForwardNavButtonEvent = () => {
      handleNavButtonEvent(NAV_BUTTON.forward);
      ForwardNavButtonAnalyticsFn();
    };

    useEffect(() => {
      if (swipeDirection) {
        switch (swipeDirection) {
          case SWIPE_DIRECTION.right:
            if (dir === 'ltr') {
              ForwardNavButtonEvent();
            } else {
              BackNavButtonEvent();
            }

            break;
          case SWIPE_DIRECTION.left:
            if (dir === 'ltr') {
              BackNavButtonEvent();
            } else {
              ForwardNavButtonEvent();
            }

            break;
          default:
            break;
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [swipeDirection]);

    if (children === undefined) {
      // eslint-disable-next-line no-console
      console.warn(`No children found for carousel: ${name}`);
      return null;
    }

    if (
      (!!linkUrl && linkCta === undefined) ||
      (!!linkCta && linkUrl === undefined)
    ) {
      if (linkCta === undefined) {
        // eslint-disable-next-line no-console
        console.warn(
          `Link CTA will not render. "linkUrl" prop provided, but "linkCta" prop missing for carousel: ${name}. Please provide both props.`,
        );
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          `Link CTA will not render. "linkCta" prop provided, but "linkUrl" prop missing for carousel: ${name}. Please provide both props.`,
        );
      }
    }

    let descriptionWrapper = description;
    if (description && typeof description === 'string') {
      descriptionWrapper = (
        <BodyOne color="onSurface.text.subdued">{description}</BodyOne>
      );
    }

    return (
      <Flex
        flexDirection="column"
        overflow="hidden"
        data-testid="carousel"
        className={className}
        ref={ref}
      >
        <Box marginBottom={titleMarginBottom}>
          <StackLayout
            orientation="horizontal"
            horizontalAlignment="start"
            verticalAlignment="top"
            spacing="$one"
            css={{
              '@mobile': {
                flexWrap: 'wrap',
              },
              '@tablet': {
                flexWrap: 'wrap',
              },
            }}
          >
            {(heading || descriptionWrapper) && (
              <StackLayout
                spacing="$quarter"
                css={{
                  flex: '1 1 auto',
                }}
              >
                {heading}
                {descriptionWrapper}
              </StackLayout>
            )}
            <StackLayout
              orientation="horizontal"
              horizontalAlignment="end"
              spacing="$threeQuarters"
              verticalAlignment="center"
              css={{
                '@mobile': {
                  flex: '1 1 40%',
                },
                flex: '1 1 350px',
              }}
            >
              {linkCta && linkUrl && (
                <CTALink
                  onClick={linkOnClick}
                  ariaLabel={linkAriaLabel}
                  href={linkUrl}
                >
                  {linkCta}
                </CTALink>
              )}
              {showNavButtons && !sideArrows && (
                <Flex padding="half" columnGap="threeQuarters">
                  <BackNavButton
                    disabled={backArrowDisabled}
                    onClick={BackNavButtonEvent}
                  />
                  <ForwardNavButton
                    disabled={forwardArrowDisabled}
                    onClick={ForwardNavButtonEvent}
                  />
                </Flex>
              )}
            </StackLayout>
          </StackLayout>
        </Box>
        <Flex
          width="100%"
          justifyContent="space-between"
          minWidth="100%"
          position="relative"
        >
          <CarouselInnerWrapper
            width="100%"
            height="100%"
            overflow="hidden"
            as="ul"
            ref={carouselRef}
            spaceBetweenCards={spaceBetweenCards}
            paddingBottom={showProgressDots ? 'four' : 'quarter'}
            padding="quarter"
            listStyles={listStyles}
          >
            {React.Children.map(children, (child, index) => (
              <li
                ref={(element) => {
                  if (element !== null) {
                    childrenRef.current[index] = element;
                  }
                  return null;
                }}
              >
                {child}
              </li>
            ))}
          </CarouselInnerWrapper>
          <VisuallyHidden aria-live="polite" aria-atomic="true">
            {slideAnnouncement}
          </VisuallyHidden>
          {sideArrows && showNavButtons && (
            <Flex
              position="absolute"
              insetInlineStart="one"
              top="50%"
              zIndex={1}
              transform="translateY(-50%)"
            >
              <BackNavButton
                disabled={backArrowDisabled}
                onClick={BackNavButtonEvent}
              />
            </Flex>
          )}
          {sideArrows && showNavButtons && (
            <Flex
              position="absolute"
              insetInlineEnd="one"
              top="50%"
              zIndex={1}
              transform="translateY(-50%)"
            >
              <ForwardNavButton
                disabled={forwardArrowDisabled}
                onClick={ForwardNavButtonEvent}
              />
            </Flex>
          )}
          {/* Only show progress dots if prop is provided and there is more than one child component. */}
          {showProgressDots && childrenLength >= 2 && (
            <ProgressDots
              currentDot={currentProgressIndex}
              length={childrenLength}
            />
          )}
        </Flex>
      </Flex>
    );
  },
);
